import { CampaignTypeDefinitions } from 'modules/campaignIndex/domainTypes';
import { CampaignType } from 'modules/domains/campaign';
import {
  AttributionModeDefinition,
  ExperimentState,
  ExperimentType,
  KVAnalyticsMetricDoubleResultDefinition,
  KVAnalyticsMetricResultDefinition,
  KVAnalyticsMetricTermsResultDefinition,
  LastMessaged,
  MessageMedium,
} from 'modules/graphql';
import {
  BLAST_FINISHED_STATES,
  BLAST_STATES_WITH_EDIT_ACTION,
  BLAST_STATES_WITH_END_ACTION,
  BLAST_STATE_WITH_USE_VARIANT_ACTION,
  BLAST_UNFINISHED_STATES,
  EDITABLE_EXPERIMENT_STATES,
  MetricIdentifier,
  TRIGGER_FINISHED_STATES,
  TRIGGER_STATES_WITH_EDIT_ACTION,
  TRIGGER_STATES_WITH_END_ACTION,
  TRIGGER_STATE_WITH_USE_VARIANT_ACTION,
  TRIGGER_UNFINISHED_STATES,
} from '../constants';
import {
  BasePageExperimentData,
  EmailVariant,
  ExperimentHoldoutBreakdownDetail,
  ExperimentVariantBreakdownDetail,
  ExperimentVariantSummary,
  ExperimentVariationInputArg,
  PushVariant,
  SMSVariant,
  SendTimeVariant,
  UpdateVariantArgs,
  VariantRowItem,
  WebPushVariant,
} from '../types';

export const isAnalyticsMetricDoubleResult = (
  metricResult: KVAnalyticsMetricResultDefinition,
): metricResult is KVAnalyticsMetricDoubleResultDefinition => !('terms' in metricResult.value);

export const isAnalyticsMetricTermsResult = (
  metricResult: KVAnalyticsMetricResultDefinition,
): metricResult is KVAnalyticsMetricTermsResultDefinition => 'terms' in metricResult.value;

export const isExperimentVariantBreakdownRow = (
  row: VariantRowItem,
): row is ExperimentVariantBreakdownDetail =>
  'variantName' in row && MetricIdentifier.SYSTEM_METRICS in row;

export const isExperimentVariantSummaryRow = (
  row: VariantRowItem,
): row is ExperimentVariantSummary => 'controlExperimentSends' in row;

export const isExperimentHoldoutRow = (
  row: VariantRowItem,
): row is ExperimentHoldoutBreakdownDetail => 'holdoutGroupPercent' in row;

export const isEmailVariationInput = (args: UpdateVariantArgs): args is EmailVariant =>
  ExperimentVariationInputArg.EMAIL in args;
export const isSmsVariationInput = (args: UpdateVariantArgs): args is SMSVariant =>
  ExperimentVariationInputArg.SMS in args;
export const isPushVariationInput = (args: UpdateVariantArgs): args is PushVariant =>
  ExperimentVariationInputArg.PUSH in args;
export const isWebPushVariationInput = (args: UpdateVariantArgs): args is WebPushVariant =>
  ExperimentVariationInputArg.WEB_PUSH in args;
export const isSendTimeVariationInput = (args: UpdateVariantArgs): args is SendTimeVariant =>
  ExperimentVariationInputArg.SEND_TIME in args;

export const isAttributionModeLastMessaged = (
  attributionMode: AttributionModeDefinition,
): attributionMode is LastMessaged =>
  typeof attributionMode === 'object' && 'periodHours' in attributionMode;

export const isBlastCampaignType = (campaignType: CampaignType) =>
  campaignType === CampaignType.Blast;

export const isTriggerCampaignType = (campaignType: CampaignType | CampaignTypeDefinitions) =>
  [
    CampaignType.ApiTriggered,
    CampaignType.WorkflowTriggered,
    CampaignTypeDefinitions.ApiTriggered,
    CampaignTypeDefinitions.WorkflowTriggered,
  ].includes(campaignType);

export const isDraftExperiment = (experimentState: ExperimentState) =>
  experimentState === ExperimentState.DRAFT;

export const isRunningExperiment = (
  campaignType: CampaignType,
  experimentState: ExperimentState,
) =>
  isTriggerCampaignType(campaignType)
    ? TRIGGER_STATES_WITH_END_ACTION.includes(experimentState)
    : BLAST_STATES_WITH_END_ACTION.includes(experimentState);

export const isEditableExperiment = (experimentState: ExperimentState) =>
  EDITABLE_EXPERIMENT_STATES.includes(experimentState);

export const getExperimentStatesWithEditAction = (
  campaignType: CampaignType | CampaignTypeDefinitions,
) =>
  isTriggerCampaignType(campaignType)
    ? TRIGGER_STATES_WITH_EDIT_ACTION
    : BLAST_STATES_WITH_EDIT_ACTION;

export const getExperimentStatesWithUseVariantAction = (campaignType: CampaignType) =>
  isTriggerCampaignType(campaignType)
    ? TRIGGER_STATE_WITH_USE_VARIANT_ACTION
    : BLAST_STATE_WITH_USE_VARIANT_ACTION;

export const getUnfinishedExperimentStates = (campaignType: CampaignType) =>
  isTriggerCampaignType(campaignType) ? TRIGGER_UNFINISHED_STATES : BLAST_UNFINISHED_STATES;

export const getFinishedExperimentStates = (campaignType: CampaignType) =>
  isTriggerCampaignType(campaignType) ? TRIGGER_FINISHED_STATES : BLAST_FINISHED_STATES;

export const isEditButtonAvailableForState = (
  campaignType: CampaignType | CampaignTypeDefinitions,
  experimentState: ExperimentState,
) => getExperimentStatesWithEditAction(campaignType).includes(experimentState);

/**
 * MAB mode is available only for Blast experiments
 * In non MAB experiments endedAt and exploitationStartedAt will be the same
 *
 * For MAB the experiment first enters the exploitation phase during this phase
 * 90% of future users will get the winning variant while Iterable will continue to test
 * with the remaining 10% and declare a new winner if new user preferences surface.
 * Once, this phase ends the experiment will have the endedAt attribute set.
 */
export const isExperimentInMABMode = (
  isTriggerCampaign: boolean,
  endedAt: BasePageExperimentData['endedAt'],
  exploitationStartedAt: BasePageExperimentData['exploitationStartedAt'],
) => isTriggerCampaign && !endedAt && !!exploitationStartedAt;

export const isEmailMessageMedium = (messageMedium: MessageMedium) =>
  messageMedium === MessageMedium.Email;
export const isInAppMessageMedium = (messageMedium: MessageMedium) =>
  messageMedium === MessageMedium.InApp;

export const isFromNameAndSenderExperiment = (type: ExperimentType) =>
  type === ExperimentType.FromNameAndSender;
export const isBodyExperiment = (type: ExperimentType) => type === ExperimentType.EmailBody;
export const isSendTimeExperiment = (type: ExperimentType) => type === ExperimentType.SendTime;
export const isSTOExperiment = (type: ExperimentType) =>
  type === ExperimentType.SendTimeOptimization;
export const isEverythingExperiment = (type: ExperimentType) => type === ExperimentType.WholeEmail;
